import { getPortalRoot } from '@src/components/portalRoot';
import {
  useEffect,
  useCallback,
  type RefObject,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Button, type ButtonSize, type ButtonVariant } from '@src/components/Button/Button';
import { MoreIcon } from '@src/components/icons/MoreIcon';
import { classes } from '@src/components/layout/space';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Menu.css';

export interface MenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Paints the item red — deleting a participant. */
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
  testId?: string;
}

export interface MenuProps {
  /** An external gesture/keyboard target instead of a visible trigger button. */
  anchorRef?: RefObject<HTMLElement | null>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  items: MenuItem[];
  /** Accessible name for the trigger, and its tooltip. */
  label: string;
  /** Trigger copy. Without it the trigger is the square ⋯ button. */
  children?: ReactNode;
  icon?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Which edge of the trigger the sheet hangs from. `end` is the default
   * because a menu at the right of a row must not open off-screen.
   */
  align?: 'start' | 'end';
  placement?: 'top' | 'bottom';
  testId?: string;
}

const VIEWPORT_PAD_PX = 8;

function readMenuGapPx(): number {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:absolute;visibility:hidden;height:var(--space-2);pointer-events:none';
  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return height || 8;
}

function menuSheetStyle(
  trigger: HTMLElement,
  sheet: HTMLElement,
  align: 'start' | 'end',
  placement: 'top' | 'bottom',
): CSSProperties {
  const gap = readMenuGapPx();
  const triggerRect = trigger.getBoundingClientRect();
  const sheetRect = sheet.getBoundingClientRect();

  let top =
    placement === 'top'
      ? Math.max(VIEWPORT_PAD_PX, triggerRect.top - sheetRect.height - gap)
      : triggerRect.bottom + gap;
  let left = align === 'end' ? triggerRect.right - sheetRect.width : triggerRect.left;

  left = Math.max(
    VIEWPORT_PAD_PX,
    Math.min(left, window.innerWidth - sheetRect.width - VIEWPORT_PAD_PX),
  );

  if (top + sheetRect.height > window.innerHeight - VIEWPORT_PAD_PX) {
    top = Math.max(VIEWPORT_PAD_PX, window.innerHeight - sheetRect.height - VIEWPORT_PAD_PX);
  }

  return {
    position: 'fixed',
    top,
    left,
    right: 'auto',
    bottom: 'auto',
  };
}

/**
 * A short list of actions hanging off a button. Not a select — picking an item
 * performs it, so nothing here is ever "current".
 *
 * One menu is open at a time: the trigger owns the open state and any click or
 * Escape outside closes it, which is also what returns focus to the trigger.
 */
export function Menu({
  anchorRef,
  open: controlledOpen,
  onOpenChange,
  items,
  label,
  children,
  icon,
  variant = 'ghost',
  size = 'small',
  align = 'end',
  placement = 'bottom',
  testId,
}: MenuProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = useCallback(
    (value: boolean) => {
      setInternalOpen(value);
      onOpenChange?.(value);
    },
    [onOpenChange],
  );
  const [sheetPosition, setSheetPosition] = useState<CSSProperties | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    function updatePosition() {
      const trigger = anchorRef?.current ?? triggerRef.current;
      const sheet = sheetRef.current;
      if (!trigger || !sheet) {
        return;
      }
      setSheetPosition(menuSheetStyle(trigger, sheet, align, placement));
    }

    updatePosition();
    const raf = requestAnimationFrame(updatePosition);

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.visualViewport?.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.visualViewport?.removeEventListener('resize', updatePosition);
    };
  }, [open, align, placement, items, anchorRef]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function onPointerDown(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) ||
        sheetRef.current?.contains(target) ||
        anchorRef?.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
        (anchorRef?.current ?? triggerRef.current)?.focus();
      }
    }

    // Capture: a row underneath must not also react to the click that dismisses.
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, anchorRef, setOpen]);

  useEffect(() => {
    if (open) {
      sheetRef.current?.querySelector<HTMLElement>('.menu__item:not([disabled])')?.focus();
    }
  }, [open, sheetPosition]);

  function moveFocus(from: HTMLElement, delta: number) {
    const nodes = Array.from(
      sheetRef.current?.querySelectorAll<HTMLElement>('.menu__item:not([disabled])') ?? [],
    );
    if (nodes.length === 0) {
      return;
    }
    const index = nodes.indexOf(from);
    const next = (index + delta + nodes.length) % nodes.length;
    nodes[next].focus();
  }

  const sheet = open ? (
    <div
      ref={sheetRef}
      className={classes(
        'menu__sheet',
        'menu__sheet--portal',
        align === 'start' && 'menu__sheet--start',
      )}
      style={sheetPosition ?? { position: 'fixed', visibility: 'hidden', top: 0, left: 0 }}
      id={menuId}
      role="menu"
      aria-label={label}
      {...testIdAttr(testIdFor(testId, 'sheet'))}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          className={classes('menu__item', item.danger && 'menu__item--danger')}
          disabled={item.disabled}
          onClick={() => {
            setOpen(false);
            item.onSelect();
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              moveFocus(event.currentTarget, 1);
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              moveFocus(event.currentTarget, -1);
            }
          }}
          {...testIdAttr(item.testId ?? testIdFor(testId, item.id))}
        >
          {item.icon ? (
            <span className="menu__icon" aria-hidden="true">
              {item.icon}
            </span>
          ) : null}
          <Typography as="span" role="label/nav" color="inherit" className="menu__label">
            {item.label}
          </Typography>
        </button>
      ))}
    </div>
  ) : null;

  if (anchorRef) return sheet ? createPortal(sheet, getPortalRoot()) : null;

  return (
    <div className="menu" ref={rootRef} {...testIdAttr(testId)}>
      {!anchorRef ? (
        <Button
          ref={triggerRef}
          variant={variant}
          size={size}
          icon={children ? icon : (icon ?? <MoreIcon />)}
          aria-label={children ? undefined : label}
          title={children ? undefined : label}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          onClick={() => setOpen(!open)}
          testId={testIdFor(testId, 'trigger')}
        >
          {children}
        </Button>
      ) : null}

      {sheet ? createPortal(sheet, getPortalRoot()) : null}
    </div>
  );
}
